import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import api from "../api/axios";
import "./PublicCoachProfile.css";
import { trackAppStoreClick, trackCoachCtaClick, trackGooglePlayClick } from "../analytics/googleAnalytics";

const APP_STORE_URL = "https://apps.apple.com/app/id6756500478";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.facundosolis.wayo";

const typeLabels = {
  guide: "Guias",
  recipe: "Recetas",
  ebook: "Ebooks",
  video: "Videos",
  program: "Programas",
};

function formatCount(value) {
  const count = Number(value) || 0;
  if (count >= 1000000) return `${(count / 1000000).toFixed(1).replace(".0", "")}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(".0", "")}k`;
  return String(count);
}

export default function PublicCoachProfile() {
  const { coachId } = useParams();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const appDeepLink = useMemo(() => `wayo://coaches/${coachId}`, [coachId]);

  useEffect(() => {
    let active = true;

    api
      .get(`/discover/coaches/${coachId}`)
      .then((res) => {
        if (!active) return;
        setCoach(res.data);
        setError(false);
      })
      .catch((err) => {
        console.error("Error loading public coach profile:", err);
        if (!active) return;
        setError(true);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [coachId]);

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent || "");
    if (!isMobile) return undefined;

    const openTimer = window.setTimeout(() => {
      window.location.href = appDeepLink;
    }, 500);

    return () => {
      window.clearTimeout(openTimer);
    };
  }, [appDeepLink]);

  const heroImage = coach?.image_url || "https://wayoai.app/og-image.jpg";
  const coachName = coach?.display_name || coach?.name || "Coach Wayo";
  const totalWorkouts = coach?.workouts?.length || 0;
  const totalContents = coach?.contents?.length || 0;
  const guideCount = coach?.contents?.filter((item) => item.type === "guide").length || 0;
  const recipeCount = coach?.contents?.filter((item) => item.type === "recipe").length || 0;
  const contentPreview = coach?.contents?.slice(0, 4) || [];

  return (
    <>
      <Helmet>
        <title>{coach ? `${coachName} | Coach en Wayo` : "Perfil de Coach | Wayo"}</title>
        <meta
          name="description"
          content={
            coach?.bio ||
            "Descubre el perfil del coach en Wayo, abre la app y accede a su contenido, entrenamientos y guias."
          }
        />
        <link rel="canonical" href={`https://wayoai.app/coaches/${coachId}`} />
        <meta property="og:title" content={coach ? `${coachName} en Wayo` : "Coach en Wayo"} />
        <meta
          property="og:description"
          content={
            coach?.specialty
              ? `${coach.specialty}. Abre el perfil completo en la app de Wayo.`
              : "Abre el perfil completo en la app de Wayo."
          }
        />
        <meta property="og:url" content={`https://wayoai.app/coaches/${coachId}`} />
        <meta property="og:image" content={heroImage} />
      </Helmet>

      <main className="public-coach-page">
        <section className="public-coach-hero">
          <div className="public-coach-backdrop" />
          <div className="public-coach-shell">
            <div className="public-coach-topbar">
              <Link to="/" className="public-coach-brand">
                Wayo
              </Link>
              <a href="#download" className="public-coach-toplink">
                Abrir en la app
              </a>
            </div>

            {loading ? (
              <div className="public-coach-loading">Cargando perfil del coach...</div>
            ) : error || !coach ? (
              <div className="public-coach-empty">
                <h1>Este perfil no esta disponible</h1>
                <p>Puede que el coach ya no sea publico o que el enlace no sea correcto.</p>
                <div className="public-coach-ctaRow">
                  <a
                    href={APP_STORE_URL}
                    className="public-coach-primaryBtn"
                    onClick={() => trackAppStoreClick("public_coach_profile_empty")}
                  >
                    Descargar para iPhone
                  </a>
                  <a
                    href={GOOGLE_PLAY_URL}
                    className="public-coach-secondaryBtn"
                    onClick={() => trackGooglePlayClick("public_coach_profile_empty")}
                  >
                    Descargar para Android
                  </a>
                </div>
              </div>
            ) : (
              <>
                <div className="public-coach-heroGrid">
                  <div className="public-coach-copy">
                    <div className="public-coach-badge">Perfil compartido desde Wayo</div>
                    <h1>{coachName}</h1>
                    {coach.specialty ? (
                      <p className="public-coach-specialty">{coach.specialty}</p>
                    ) : null}
                    <p className="public-coach-bio">{coach.bio || "Coach verificado en Wayo."}</p>

                    <div className="public-coach-stats">
                      <div className="public-coach-statCard">
                        <span>{formatCount(coach.followers_count)}</span>
                        <small>Seguidores</small>
                      </div>
                      <div className="public-coach-statCard">
                        <span>{formatCount(coach.likes_count)}</span>
                        <small>Likes</small>
                      </div>
                      <div className="public-coach-statCard">
                        <span>{formatCount(coach.works_count)}</span>
                        <small>Contenidos</small>
                      </div>
                    </div>

                    <div className="public-coach-ctaRow" id="download">
                      <a
                        href={appDeepLink}
                        className="public-coach-primaryBtn"
                        onClick={() => trackCoachCtaClick("public_coach_profile_open_app")}
                      >
                        Abrir perfil en Wayo
                      </a>
                      <a
                        href={APP_STORE_URL}
                        className="public-coach-secondaryBtn"
                        onClick={() => trackAppStoreClick("public_coach_profile")}
                      >
                        App Store
                      </a>
                      <a
                        href={GOOGLE_PLAY_URL}
                        className="public-coach-secondaryBtn"
                        onClick={() => trackGooglePlayClick("public_coach_profile")}
                      >
                        Google Play
                      </a>
                    </div>
                  </div>

                  <div className="public-coach-visual">
                    <div className="public-coach-phoneFrame">
                      <img src={heroImage} alt={coachName} className="public-coach-avatar" />
                      <div className="public-coach-phoneCard">
                        <strong>@{coachName}</strong>
                        <span>
                          {totalWorkouts} entrenamientos · {totalContents} contenidos
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <section className="public-coach-section">
                  <div className="public-coach-sectionHeader">
                    <h2>Lo que vas a encontrar en su perfil</h2>
                    <p>
                      Puedes ver su perfil publico ahora mismo. Para desbloquear contenido premium,
                      mensajes y planes personalizados, el usuario necesitara Premium dentro de la app.
                    </p>
                  </div>

                  <div className="public-coach-featureGrid">
                    <article className="public-coach-featureCard">
                      <span>Entrenamientos</span>
                      <strong>{totalWorkouts}</strong>
                      <p>Rutinas y sesiones publicadas por el coach.</p>
                    </article>
                    <article className="public-coach-featureCard">
                      <span>Guias</span>
                      <strong>{guideCount}</strong>
                      <p>Guias y recursos para profundizar en sus metodos.</p>
                    </article>
                    <article className="public-coach-featureCard">
                      <span>Recetas</span>
                      <strong>{recipeCount}</strong>
                      <p>Ideas practicas para comer mejor y sostener objetivos.</p>
                    </article>
                  </div>
                </section>

                {contentPreview.length > 0 ? (
                  <section className="public-coach-section">
                    <div className="public-coach-sectionHeader">
                      <h2>Vista previa del contenido</h2>
                      <p>Una muestra de lo que podras explorar dentro de Wayo.</p>
                    </div>

                    <div className="public-coach-contentGrid">
                      {contentPreview.map((content) => (
                        <article className="public-coach-contentCard" key={content.id}>
                          {content.thumbnail_url ? (
                            <img src={content.thumbnail_url} alt={content.title} className="public-coach-contentImage" />
                          ) : (
                            <div className="public-coach-contentPlaceholder">Wayo</div>
                          )}
                          <div className="public-coach-contentBody">
                            <div className="public-coach-contentType">
                              {typeLabels[content.type] || "Contenido"}
                            </div>
                            <h3>{content.title}</h3>
                            {content.description ? <p>{content.description}</p> : null}
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ) : null}
              </>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
